import { ajax } from "../utils/async";
import { ERR_INVALID_STATE, rejectError } from "../utils/error";
import { ListenableEvent } from "../utils/ListenableEvent";
import { isNotNullOrEmpty, isNullOrEmpty } from "../utils/string";
import { BasketService } from "./basket";
import { CatalogService } from "./catalog";
import { ProjectService } from "./project";
import { SessionService, StringPair } from "./session";
var EaiwsSession = /** @class */ (function () {
    function EaiwsSession() {
        /**
         * Will be triggered if this instance was connected to an EAIWS session.
         */
        this.eventConnected = new ListenableEvent(this);
        /**
         * Will be triggered if this instance was disconnected from an EAIWS session.
         */
        this.eventDisconnected = new ListenableEvent(this);
        /**
         * Will be triggered from `session.setLocale()`.
         */
        this.eventSessionLocaleChanged = new ListenableEvent(this);
        /**
         * Will be triggered from `catalog.setLanguages()`.
         */
        this.eventCatalogLanguagesChanged = new ListenableEvent(this);
        /**
         * Will be triggered from `basket.setLanguages()`.
         */
        this.eventBasketLanguagesChanged = new ListenableEvent(this);
        this.mBaseUrl = undefined;
        this.mSessionId = undefined;
        this.mSession = undefined;
        this.mCatalog = undefined;
        this.mBasket = undefined;
        this.mProject = undefined;
        this.mKeepAliveInterval = null;
    }
    Object.defineProperty(EaiwsSession.prototype, "baseUrl", {
        get: function () {
            return (this.mBaseUrl);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EaiwsSession.prototype, "sessionId", {
        get: function () {
            return (this.mSessionId);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EaiwsSession.prototype, "session", {
        get: function () {
            return (this.mSession);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EaiwsSession.prototype, "catalog", {
        get: function () {
            return (this.mCatalog);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EaiwsSession.prototype, "basket", {
        get: function () {
            return (this.mBasket);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EaiwsSession.prototype, "project", {
        get: function () {
            return (this.mProject);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EaiwsSession.prototype, "isValid", {
        get: function () {
            return (this.mSession != null);
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Opens and connects a new EAIWS session.
     */
    EaiwsSession.prototype.open = function (pBaseUrl, pOptions) {
        var _this = this;
        var tArgs = new Array();
        if (isNotNullOrEmpty(pOptions.startup)) {
            tArgs.push(new StringPair("-startup", pOptions.startup));
        }
        if (isNotNullOrEmpty(pOptions.packageGroups)) {
            tArgs.push(new StringPair("-package-groups", pOptions.packageGroups));
        }
        if (isNotNullOrEmpty(pOptions.userToken)) {
            tArgs.push(new StringPair("-user-token", pOptions.userToken));
        }
        if (isNotNullOrEmpty(pOptions.locale)) {
            tArgs.push(new StringPair("-locale", pOptions.locale));
        }
        if (isNotNullOrEmpty(pOptions.dataDomain)) {
            tArgs.push(new StringPair("-domain", pOptions.dataDomain));
        }
        var tPromise = SessionService.OpenSession(pBaseUrl, tArgs).then(function (pId) {
            return (_this.connect(pBaseUrl, pId, pOptions.keepAliveInterval));
        });
        return (tPromise);
    };
    /**
     * Closes the currently connected EAIWS session and disconnects.
     */
    EaiwsSession.prototype.close = function () {
        var _this = this;
        if (this.mSession == null)
            return (Promise.resolve(false));
        var tPromise = this.mSession.closeSession().then(function () {
            return (_this.disconnect());
        });
        return (tPromise);
    };
    /**
     * Connects to an already opened EAIWS session. If pKeepAliveInterval is null or 0 the keep
     * alive handling will be disabled (default: 60000).
     */
    EaiwsSession.prototype.connect = function (pBaseUrl, pSessionId, pKeepAliveInterval) {
        var _this = this;
        if (pKeepAliveInterval === void 0) { pKeepAliveInterval = 60000; }
        if (isNullOrEmpty(pSessionId))
            return (false);
        this.disconnect();
        this.mBaseUrl = pBaseUrl;
        this.mSessionId = pSessionId;
        this.mSession = new SessionService(this, this.mBaseUrl, this.mSessionId);
        this.mCatalog = new CatalogService(this, this.mBaseUrl, this.mSessionId);
        this.mBasket = new BasketService(this, this.mBaseUrl, this.mSessionId);
        this.mProject = new ProjectService(this, this.mBaseUrl, this.mSessionId);
        if (pKeepAliveInterval != null && pKeepAliveInterval > 0) {
            this.mKeepAliveInterval = window.setInterval(function () {
                _this.keepAlive();
            }, pKeepAliveInterval);
        }
        this.eventConnected.trigger();
        return (true);
    };
    /**
     * Disconnects from the currently connected EAIWS session. The session will not be closed.
     */
    EaiwsSession.prototype.disconnect = function () {
        if (this.mSession == null)
            return (false);
        this.mBaseUrl = undefined;
        this.mSessionId = undefined;
        this.mSession = undefined;
        this.mCatalog = undefined;
        this.mBasket = undefined;
        this.mProject = undefined;
        if (this.mKeepAliveInterval != null) {
            clearInterval(this.mKeepAliveInterval);
            this.mKeepAliveInterval = null;
        }
        this.eventDisconnected.trigger();
        return (true);
    };
    EaiwsSession.prototype.uploadFileToUrl = function (pTargetUrl, pFile, pOnProgress) {
        var tPromise = new Promise(function (pResolve, pReject) {
            var tXhr = new XMLHttpRequest();
            tXhr.open("PUT", pTargetUrl);
            tXhr.onreadystatechange = function () {
                if (tXhr.readyState === 4) {
                    if (tXhr.status >= 200 && tXhr.status < 300) {
                        pResolve(pTargetUrl);
                    }
                    else if (tXhr.status === 0) { //aborted
                        pResolve("");
                    }
                    else { // Failed
                        pReject(new Error(tXhr.status.toString()));
                    }
                }
            };
            if (pOnProgress != null && tXhr.upload != null)
                tXhr.upload.onprogress = function (pEvent) { return pOnProgress(pEvent, tXhr); };
            tXhr.setRequestHeader("Content-Type", pFile.type);
            tXhr.responseType = "text";
            tXhr.send(pFile);
        });
        return (tPromise);
    };
    EaiwsSession.prototype.uploadFile = function (pType, pFile, pOnProgress, pUrlSuffix) {
        var _this = this;
        if (!this.isValid)
            return (rejectError(ERR_INVALID_STATE, "session not valid"));
        var tPromise = this.mSession.getUploadURL(pType, pUrlSuffix).then(function (pUrl) {
            if (pUrl != null && pUrl !== "") {
                return (_this.uploadFileToUrl(pUrl, pFile, pOnProgress));
            }
            else {
                return (Promise.resolve(null));
            }
        });
        return (tPromise);
    };
    EaiwsSession.prototype.deleteFile = function (pUrl) {
        return (ajax("DELETE", pUrl, null, { responseType: "text" }));
    };
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    EaiwsSession.prototype._clearCache = function () {
        if (this.isValid) {
            this.mSession._clearCache();
            this.mCatalog._clearCache();
            this.mBasket._clearCache();
            this.mProject._clearCache();
        }
    };
    EaiwsSession.prototype.keepAlive = function () {
        if (this.isValid)
            this.mSession.keepAlive();
    };
    return EaiwsSession;
}());
export { EaiwsSession };
